import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function AssessmentPlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [isLoading, setIsLoading] = useState(true);

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      sessionId: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
      assessmentPlanDetail: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "assessmentPlanDetail",
  });

  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [assessmentPlans, setAssessmentPlans] = useState([]);

  useEffect(() => {
    const fetchFeePlans = async () => {
      try {
        const response = await get(
          `AssessmentPlan/GetAllAssessmentPlanByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setAssessmentPlans(response);
      } catch (error) {
        console.error("Error fetching assessmentPlans:", error);
      }
    };

    fetchFeePlans();
  }, [triggerRerender]);

  useEffect(() => {
    const fetchAssessmentTypes = async () => {
      try {
        const response = await get(
          `AssessmentType/GetAssessmentTypeByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setAssessmentTypes(response.assessmentType);

        const assessmentPlanDetail = response.assessmentType.map(
          (assessmentType) => ({
            assessmentTypeId: assessmentType.id || "",
            totalScore: assessmentType.totalScore || "",
            passingScorePercentage: assessmentType.passingScorePercentage || "",
            category: assessmentType.category || "",
            remarks: assessmentType.remarks || "",
            createdBy: localStorage.getItem("userId"),
          })
        );

        setValue("assessmentPlanDetail", assessmentPlanDetail);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching assessmentType:", error);
      }
    };

    fetchAssessmentTypes();
  }, [triggerRerender]);

  useEffect(() => {
    const getSessionsList = async () => {
      if (!token) {
        console.error("Unauthorized.");
        return;
      }

      try {
        const response = await get(
          `Session/GetSessions?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSessionsData(response.sessions);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };

    getSessionsList();
  }, []);

  const handleEditClick = async (rowId) => {
    const singleAssessmentPlan = await get(
      `AssessmentPlan/GetAssessmentPlanByIdAsync?AssessmentPlanId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const selectedSession = sessionsData.find(
      (session) => session.id === singleAssessmentPlan.sessionId
    );

    const assessmentPlanDetailMap =
      singleAssessmentPlan.assessmentPlanDetail.reduce((map, detail) => {
        map[detail.assessmentTypeId] = detail;
        return map;
      }, {});

    const mappedAssessmentPlanDetails = assessmentTypes.map(
      (assessmentType) => {
        return {
          assessmentTypeId: assessmentType.id,
          totalScore:
            assessmentPlanDetailMap[assessmentType.id]?.totalScore || "",
          passingScorePercentage:
            assessmentPlanDetailMap[assessmentType.id]
              ?.passingScorePercentage || "",
          category: assessmentPlanDetailMap[assessmentType.id]?.category || "",
          remarks: assessmentPlanDetailMap[assessmentType.id]?.remarks || "",
          createdBy:
            assessmentPlanDetailMap[assessmentType.id]?.createdBy ||
            localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
        };
      }
    );

    reset({
      id: singleAssessmentPlan.id || 0,
      name: singleAssessmentPlan.name || "",
      sessionId: selectedSession
        ? { value: selectedSession.id, label: selectedSession.name }
        : null,
      createdBy:
        singleAssessmentPlan.createdBy || localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      organizationId:
        singleAssessmentPlan.organizationId ||
        localStorage.getItem("organizationId"),
      assessmentPlanDetail: mappedAssessmentPlanDetails,
    });

    // Clear and re-append rows in the form field array
    remove(); // Clear existing rows
    mappedAssessmentPlanDetails.forEach((detail) => {
      append(detail); // Add the correctly mapped rows
    });
  };

  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
    },
    {
      id: "actions",
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEditClick(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = assessmentPlans.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AssessmentPlans");
    XLSX.writeFile(workbook, "AssessmentPlans.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Assessment Plans List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [["Sr No", "Name"]];

    const bodyData = assessmentPlans.map((row, index) => [index + 1, row.name]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("AssessmentPlans.pdf");
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      sessionId: data.sessionId?.value,
    };

    try {
      const response = await post(
        "AssessmentPlan/AddUpdateAssessmentPlanAsync",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          sessionId: "",
          createdBy: localStorage.getItem("userId"),
          organizationId: localStorage.getItem("organizationId"),
          assessmentPlanDetail: [],
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  if (isLoading) {
    return <div className="fa fa-spinner fs-7"></div>;
  }

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Assessment Plan</h2>
        {/* <div className="ms-auto">
          <Link
            to="/organization-form"
            className="btn btn-outline-primary btn-sm"
          >
            Add New
          </Link>
        </div> */}
      </div>
      <div className="row g-3">
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <form
                className="new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`name`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck("name")}
                    />
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Academic Year / Session
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`sessionId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionsData.map((session) => ({
                            value: session.id,
                            label: session.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`sessionId`, null);
                            } else {
                              setValue(
                                `sessionId`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`sessionId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.sessionId && (
                      <small className="text-danger">
                        {errors.sessionId.message}
                      </small>
                    )}
                  </div>
                  <div className="w-100 m-0"></div>
                  <div className="col-md-12">
                    <table className="table table-bordered custom-table m-0">
                      <thead>
                        <tr>
                          <th>Assessment Type</th>
                          <th>Passing Score (%)</th>
                          <th>Total Score</th>
                          <th>Category</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessmentTypes?.map((assessmentType, index) => (
                          <tr key={index}>
                            <td>{assessmentType.name}</td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                {...register(
                                  `assessmentPlanDetail[${index}].passingScorePercentage`,
                                  {
                                    value:
                                      fields[index]?.passingScorePercentage ||
                                      "",
                                    maxLength: {
                                      value: 75,
                                      message:
                                        "Maximum of 75 characters allowed.",
                                    },
                                  }
                                )}
                                onInput={(e) => {
                                  if (e.target.value < 0) {
                                    e.target.value = 0;
                                  }
                                }}
                                onChange={handleChangeOfInputWithLengthCheck(
                                  `assessmentPlanDetail[${index}].passingScorePercentage`
                                )}
                              />
                              {errors.assessmentPlanDetail?.[index]
                                ?.passingScorePercentage && (
                                <div className="text-danger">
                                  {
                                    errors.assessmentPlanDetail[index]
                                      .passingScorePercentage.message
                                  }
                                </div>
                              )}
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                {...register(
                                  `assessmentPlanDetail[${index}].totalScore`,
                                  {
                                    value: fields[index]?.totalScore || "",
                                    maxLength: {
                                      value: 75,
                                      message:
                                        "Maximum of 75 characters allowed.",
                                    },
                                  }
                                )}
                                onInput={(e) => {
                                  if (e.target.value < 0) {
                                    e.target.value = 0;
                                  }
                                }}
                                onChange={handleChangeOfInputWithLengthCheck(
                                  `assessmentPlanDetail[${index}].totalScore`
                                )}
                              />
                              {errors.assessmentPlanDetail?.[index]
                                ?.totalScore && (
                                <div className="text-danger">
                                  {
                                    errors.assessmentPlanDetail[index]
                                      .totalScore.message
                                  }
                                </div>
                              )}
                            </td>
                            <td>
                              <select
                                className="form-select"
                                {...register(
                                  `assessmentPlanDetail[${index}].category`,
                                  {
                                    value: fields[index]?.category || "",
                                  }
                                )}
                                style={{ width: "90px" }}
                              >
                                <option value="">Select</option>
                                <option value="Written">Written</option>
                                <option value="Oral">Oral</option>
                                <option value="Practical">Practical</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                {...register(
                                  `assessmentPlanDetail[${index}].remarks`,
                                  {
                                    value: fields[index]?.remarks || "",
                                    maxLength: {
                                      value: 250,
                                      message:
                                        "Maximum of 250 characters allowed.",
                                    },
                                  }
                                )}
                                onChange={handleChangeOfInputWithLengthCheck(
                                  `assessmentPlanDetail[${index}].remarks`
                                )}
                              />
                              {errors.assessmentPlanDetail?.[index]
                                ?.remarks && (
                                <div className="text-danger">
                                  {
                                    errors.assessmentPlanDetail[index].remarks
                                      .message
                                  }
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Save" : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={assessmentPlans}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{ columnPinning: { right: ["actions"] } }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AssessmentPlan;
